import { supabaseCustomRole } from "@/lib/supabase/customRoleClient";

/**
 * Service for fetching dealer statistics
 */

export interface DealerStats {
  totalDealers: number;
  activatedDealers: number;
  verifiedDealers: number;
}

/**
 * Fetch dealer statistics from the database
 * Uses the dealers table directly to get accurate counts
 */
export const fetchDealerStats = async (): Promise<DealerStats> => {
  try {
    console.log("[DealerStats] Fetching dealer statistics from dealers table");
    
    // Get total count of ALL dealers (no filters)
    const { count: totalCount, error: totalError } = await supabaseCustomRole
      .from("dealers")
      .select("*", { count: "exact", head: true });

    if (totalError) {
      console.error("[DealerStats] Error fetching total dealers:", totalError);
      throw totalError;
    }

    // Get count of activated dealers
    const { count: activatedCount, error: activatedError } = await supabaseCustomRole
      .from("dealers")
      .select("*", { count: "exact", head: true })
      .eq("activated", true);

    if (activatedError) {
      console.error("[DealerStats] Error fetching activated dealers:", activatedError);
      throw activatedError;
    }

    // Get count of verified dealers (all dealers)
    const { count: verifiedCount, error: verifiedError } = await supabaseCustomRole
      .from("dealers")
      .select("*", { count: "exact", head: true });

    if (verifiedError) {
      console.error("[DealerStats] Error fetching verified dealers:", verifiedError);
      throw verifiedError;
    }

    const stats: DealerStats = {
      totalDealers: totalCount || 0,
      activatedDealers: activatedCount || 0,
      verifiedDealers: verifiedCount || 0
    };

    console.log("[DealerStats] Successfully fetched dealer statistics:", stats);
    return stats;
  } catch (error) {
    console.error("[DealerStats] Error fetching dealer statistics:", error);
    // Return fallback values in case of error
    return {
      totalDealers: 500,
      activatedDealers: 500,
      verifiedDealers: 500
    };
  }
};

/**
 * Server-side version of fetchDealerStats for SSR
 * Uses the dealers table directly to get accurate counts
 */
export const fetchServerDealerStats = async (): Promise<DealerStats> => {
  try {
    console.log("[Server][DealerStats] Fetching dealer statistics from dealers table");
    
    // Get total count of ALL dealers (no filters)
    const { count: totalCount, error: totalError } = await supabaseCustomRole
      .from("dealers")
      .select("*", { count: "exact", head: true });

    if (totalError) {
      console.error("[Server][DealerStats] Error fetching total dealers:", totalError);
      throw totalError;
    }

    // Get count of activated dealers
    const { count: activatedCount, error: activatedError } = await supabaseCustomRole
      .from("dealers")
      .select("*", { count: "exact", head: true })
      .eq("activated", true);

    if (activatedError) {
      console.error("[Server][DealerStats] Error fetching activated dealers:", activatedError);
      throw activatedError;
    }

    // Get count of verified dealers (all dealers)
    const { count: verifiedCount, error: verifiedError } = await supabaseCustomRole
      .from("dealers")
      .select("*", { count: "exact", head: true });

    if (verifiedError) {
      console.error("[Server][DealerStats] Error fetching verified dealers:", verifiedError);
      throw verifiedError;
    }

    const stats: DealerStats = {
      totalDealers: totalCount || 0,
      activatedDealers: activatedCount || 0,
      verifiedDealers: verifiedCount || 0
    };

    console.log("[Server][DealerStats] Successfully fetched dealer statistics:", stats);
    return stats;
  } catch (error) {
    console.error("[Server][DealerStats] Error fetching dealer statistics:", error);
    // Return fallback values in case of error
    return {
      totalDealers: 500,
      activatedDealers: 500,
      verifiedDealers: 500
    };
  }
};
